"use client";

import { useRef, useState } from "react";
import {
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { NashView } from "@/components/tools/econ-micro/nash-view";
import {
  type Axis,
  type Cells,
  cellKey,
  PayoffGrid,
} from "@/components/tools/econ-micro/payoff-grid";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import type { Game } from "@/lib/tool-kit/econ-micro/game";

/** English strings. A translated content file overrides these through `ui`. */
export const NASH_EQUILIBRIUM_CALCULATOR_STRINGS = {
  playersHeading: "Players",
  rowPlayer: "Row player",
  colPlayer: "Column player",
  defaultRowPlayer: "Row player",
  defaultColPlayer: "Column player",
  exampleLabel: "Load an example game",
  exampleChoose: "Choose a game…",
  exampleDilemma: "Prisoner’s dilemma",
  exampleBattle: "Battle of the sexes",
  examplePennies: "Matching pennies",
  exampleStag: "Stag hunt",
  exampleChicken: "Chicken",
  strategiesDilemma: ["Cooperate", "Defect"],
  strategiesBattle: ["Opera", "Football"],
  strategiesPennies: ["Heads", "Tails"],
  strategiesStag: ["Stag", "Hare"],
  strategiesChicken: ["Swerve", "Straight"],
  gridHeading: "Payoff matrix",
  gridHint:
    "Each cell holds two payoffs: first the row player’s, then the column player’s. Name the strategies on the edges and add up to five for each player.",
  gridCaption: "Payoffs: {row} first, then {col}",
  newRowStrategy: "Row strategy {n}",
  newColStrategy: "Column strategy {n}",
  rowStrategyName: "Name of row strategy {n}",
  colStrategyName: "Name of column strategy {n}",
  payoffLabel: "Payoff to {player} when {rowStrategy} meets {colStrategy}",
  removeStrategy: "Remove strategy {strategy}",
  addStrategy: "Add a strategy for {player}",
  results: "Results",
  invalidInput: "Every payoff needs to be a number. Check: {cells}.",
  cellName: "{rowStrategy} × {colStrategy}",
  listJoin: ", ",
  tableHeading: "Best responses",
  tableCaption: "Payoffs for {row} and {col}, best responses underlined",
  tableNote:
    "A payoff is underlined when it is the best that player can get against the other’s choice. A shaded cell has both payoffs underlined: neither player wants to switch, so it is a Nash equilibrium. Ties count as best responses.",
  equilibriumMark: "(Nash equilibrium)",
  pureHeading: "Pure-strategy Nash equilibria",
  pureNone:
    "No pair of pure strategies is an equilibrium: in every cell, at least one player would switch.",
  outcome: "{rowStrategy}, {colStrategy}",
  payoffsLabel: "Payoffs {row} and {col}",
  paretoNote:
    "The equilibrium ({rowStrategy}, {colStrategy}) is not the best outcome for the players: both would be strictly better off at {better}.",
  dominanceHeading: "Dominant strategies",
  dominantStrict:
    "{player} has a strictly dominant strategy, {strategy}: it is better than every alternative whatever the other player does.",
  dominantWeak:
    "{player} has a weakly dominant strategy, {strategy}: it is never worse than any alternative and sometimes better.",
  dominantNone: "{player} has no dominant strategy.",
  eliminationSolved:
    "Removing strictly dominated strategies one after another leaves a single outcome, ({rowStrategy}, {colStrategy}), so the game is dominance solvable.",
  eliminationPartial:
    "Removing strictly dominated strategies one after another leaves rows {rows} and columns {cols}.",
  eliminationNone:
    "No strategy is strictly dominated by another pure strategy.",
  mixedHeading: "Mixed-strategy equilibrium",
  mixedResult:
    "{row} plays {rowStrategy} with probability {rowPercent} and {col} plays {colStrategy} with probability {colPercent}.",
  mixedPayoffs:
    "Expected payoffs in this equilibrium: {rowPayoff} and {colPayoff}.",
  mixedWhy:
    "Each player mixes so that the other is indifferent between their two strategies, which is why neither can gain by changing the odds.",
  mixedNone:
    "There is no equilibrium in which both players randomize between their two strategies.",
  mixedLarger:
    "Mixed equilibria are only calculated for 2 × 2 games. Every finite game has at least one Nash equilibrium, in mixed strategies if there is no pure one.",
} as const;

export type NashEquilibriumStrings = typeof NASH_EQUILIBRIUM_CALCULATOR_STRINGS;

type ExampleId = "dilemma" | "battle" | "pennies" | "stag" | "chicken";

/** Payoff pairs [row, column] per cell, rows then columns. */
const EXAMPLES: Record<ExampleId, number[][][]> = {
  dilemma: [
    [
      [3, 3],
      [0, 5],
    ],
    [
      [5, 0],
      [1, 1],
    ],
  ],
  battle: [
    [
      [2, 1],
      [0, 0],
    ],
    [
      [0, 0],
      [1, 2],
    ],
  ],
  pennies: [
    [
      [1, -1],
      [-1, 1],
    ],
    [
      [-1, 1],
      [1, -1],
    ],
  ],
  stag: [
    [
      [4, 4],
      [0, 3],
    ],
    [
      [3, 0],
      [2, 2],
    ],
  ],
  chicken: [
    [
      [0, 0],
      [-1, 1],
    ],
    [
      [1, -1],
      [-10, -10],
    ],
  ],
};

const FIRST_FREE_ID = 100;

/** Strategy axes and typed cells for an example game. */
function buildExample(
  id: ExampleId,
  names: readonly string[],
  next: () => number
): { rows: Axis[]; cols: Axis[]; cells: Cells } {
  const rows = names.map((name) => ({ id: next(), name }));
  const cols = names.map((name) => ({ id: next(), name }));
  const cells: Cells = {};
  for (const [i, row] of rows.entries()) {
    for (const [j, col] of cols.entries()) {
      const [a, b] = EXAMPLES[id][i][j];
      cells[cellKey(row, col)] = [String(a), String(b)];
    }
  }
  return { rows, cols, cells };
}

/** The game, or the cells that are not numbers. */
function readGame(
  rows: Axis[],
  cols: Axis[],
  cells: Cells,
  name: (axis: Axis, index: number, side: "row" | "col") => string,
  cellName: string
): { game: Game } | { bad: string[] } {
  const row: number[][] = [];
  const col: number[][] = [];
  const bad: string[] = [];
  for (const [i, r] of rows.entries()) {
    row.push([]);
    col.push([]);
    for (const [j, c] of cols.entries()) {
      const pair = cells[cellKey(r, c)] ?? ["", ""];
      const [a, b] = pair.map(parseNumberInput);
      if (a === null || b === null) {
        bad.push(
          t(cellName, {
            rowStrategy: name(r, i, "row"),
            colStrategy: name(c, j, "col"),
          })
        );
      }
      row[i].push(a ?? 0);
      col[i].push(b ?? 0);
    }
  }
  return bad.length > 0 ? { bad } : { game: { row, col } };
}

export default function NashEquilibriumCalculatorWidget() {
  const strings = useToolUi(NASH_EQUILIBRIUM_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const nextId = useRef(FIRST_FREE_ID);
  const fresh = () => {
    nextId.current += 1;
    return nextId.current;
  };
  const [grid, setGrid] = useState(() => {
    let id = 0;
    return buildExample("dilemma", strings.strategiesDilemma, () => {
      id += 1;
      return id;
    });
  });
  const [players, setPlayers] = useState<[string, string]>([
    strings.defaultRowPlayer,
    strings.defaultColPlayer,
  ]);
  const rowName = players[0].trim() || strings.defaultRowPlayer;
  const colName = players[1].trim() || strings.defaultColPlayer;
  const { rows, cols, cells } = grid;

  const names: Record<ExampleId, readonly string[]> = {
    dilemma: strings.strategiesDilemma,
    battle: strings.strategiesBattle,
    pennies: strings.strategiesPennies,
    stag: strings.strategiesStag,
    chicken: strings.strategiesChicken,
  };
  const label = (axis: Axis, index: number, side: "row" | "col") =>
    axis.name.trim() === ""
      ? t(side === "row" ? strings.newRowStrategy : strings.newColStrategy, {
          n: index + 1,
        })
      : axis.name;

  const rename = (side: "row" | "col", id: number, name: string) =>
    setGrid((g) => ({
      ...g,
      [side === "row" ? "rows" : "cols"]: g[
        side === "row" ? "rows" : "cols"
      ].map((axis) => (axis.id === id ? { ...axis, name } : axis)),
    }));
  const setCell = (key: string, index: 0 | 1, value: string) =>
    setGrid((g) => {
      const pair: [string, string] = [...(g.cells[key] ?? ["", ""])];
      pair[index] = value;
      return { ...g, cells: { ...g.cells, [key]: pair } };
    });
  const add = (side: "row" | "col") =>
    setGrid((g) => {
      const axis = {
        id: fresh(),
        name: t(
          side === "row" ? strings.newRowStrategy : strings.newColStrategy,
          {
            n: (side === "row" ? g.rows : g.cols).length + 1,
          }
        ),
      };
      const nextCells = { ...g.cells };
      const others = side === "row" ? g.cols : g.rows;
      for (const other of others) {
        nextCells[
          side === "row" ? cellKey(axis, other) : cellKey(other, axis)
        ] = ["0", "0"];
      }
      return side === "row"
        ? { ...g, rows: [...g.rows, axis], cells: nextCells }
        : { ...g, cols: [...g.cols, axis], cells: nextCells };
    });
  const remove = (side: "row" | "col", id: number) =>
    setGrid((g) => ({
      ...g,
      rows: side === "row" ? g.rows.filter((a) => a.id !== id) : g.rows,
      cols: side === "col" ? g.cols.filter((a) => a.id !== id) : g.cols,
    }));
  const load = (id: ExampleId | "") => {
    if (id !== "") {
      setGrid(buildExample(id, names[id], fresh));
    }
  };

  const read = readGame(rows, cols, cells, label, strings.cellName);

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.playersHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id="ne-row-player"
            label={strings.rowPlayer}
            onChange={(v) => setPlayers((p) => [v, p[1]])}
            value={players[0]}
          />
          <TextField
            id="ne-col-player"
            label={strings.colPlayer}
            onChange={(v) => setPlayers((p) => [p[0], v])}
            value={players[1]}
          />
          <SelectField
            id="ne-example"
            label={strings.exampleLabel}
            onChange={load}
            options={[
              { value: "", label: strings.exampleChoose },
              { value: "dilemma", label: strings.exampleDilemma },
              { value: "battle", label: strings.exampleBattle },
              { value: "pennies", label: strings.examplePennies },
              { value: "stag", label: strings.exampleStag },
              { value: "chicken", label: strings.exampleChicken },
            ]}
            value=""
          />
        </div>
      </section>
      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.gridHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.gridHint}</p>
        <PayoffGrid
          cells={cells}
          colName={colName}
          cols={cols}
          onAdd={add}
          onCell={setCell}
          onRemove={remove}
          onRename={rename}
          rowName={rowName}
          rows={rows}
          strings={strings}
        />
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {"bad" in read ? (
          <ErrorNotice>
            {t(strings.invalidInput, {
              cells: read.bad.join(strings.listJoin),
            })}
          </ErrorNotice>
        ) : (
          <NashView
            colName={colName}
            colStrategies={cols.map((c, j) => label(c, j, "col"))}
            fmt={fmt}
            game={read.game}
            rowName={rowName}
            rowStrategies={rows.map((r, i) => label(r, i, "row"))}
            strings={strings}
          />
        )}
      </section>
    </div>
  );
}
